import { useCallback, useEffect, useMemo, useState } from 'react'
import { endOfMonth, startOfMonth, subMonths } from 'date-fns'
import { ListFilterDrawer } from '@/components/ui/ListFilterDrawer'
import { type PresetRange } from '@/components/ui/AdvancedDateRangePicker'
import {
  SearchableSelect,
  type SearchableSelectOption,
} from '@/components/ui/SearchableSelect'
import { useAppSelector } from '@/app/hooks'
import { selectIsAuthenticated, selectUser } from '@/features/auth/authSlice'
import {
  getCompanies,
  getProperties,
} from '@/features/invitee/services/inviteeService'
import { useUserRole } from '@/hooks/useUserRole'
import { formatDateForAPI } from '@/features/invitee/utils/inviteeFormatters'
import { ReportDateRangePicker } from './ReportDateRangePicker'
import type { MrgReportFilterState } from '../types/filters'
import type { MrgReportFiltersProps } from '../types/components'
import { RENTAL_STATUS_OPTIONS } from '../constants/filterOptions'
import { LABEL_CLASS } from '../constants/configuredReportStyles'

function mrgContractPresets(): PresetRange[] {
  const now = new Date()
  const y = now.getFullYear()
  const lastMonth = subMonths(now, 1)
  return [
    {
      label: 'Last Month',
      getValue: () => [startOfMonth(lastMonth), endOfMonth(lastMonth)],
    },
    {
      label: 'Last 12 Months',
      getValue: () => [
        startOfMonth(subMonths(now, 12)),
        endOfMonth(subMonths(now, 1)),
      ],
    },
    {
      label: String(y),
      getValue: () => [new Date(y, 0, 1), new Date(y, 11, 31)],
    },
    {
      label: String(y - 1),
      getValue: () => [new Date(y - 1, 0, 1), new Date(y - 1, 11, 31)],
    },
    {
      label: String(y - 2),
      getValue: () => [new Date(y - 2, 0, 1), new Date(y - 2, 11, 31)],
    },
  ]
}

export function MrgReportFilters({
  filters,
  isOpen,
  onClose,
  onApply,
  onReset,
}: MrgReportFiltersProps) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const user = useAppSelector(selectUser)
  const { companyId, propertyId } = useUserRole()
  const lockedCompanyId = companyId != null ? String(companyId) : undefined
  const lockedPropertyId = propertyId != null ? String(propertyId) : undefined

  const [draft, setDraft] = useState<MrgReportFilterState>(filters)
  const [companies, setCompanies] = useState<SearchableSelectOption[]>([])
  const [properties, setProperties] = useState<SearchableSelectOption[]>([])
  const contractPresets = useMemo(() => mrgContractPresets(), [])

  const loadCompanies = useCallback(async (search: string) => {
    const list = await getCompanies(search)
    setCompanies(
      list.map((c) => ({
        value: String(c.company_id),
        label: c.company_name ?? String(c.company_id),
      })),
    )
  }, [])

  const loadProperties = useCallback(
    async (search: string) => {
      const list = await getProperties(search, draft.company_id || undefined)
      setProperties(
        list.map((p) => ({
          value: String(p.property_id),
          label: p.property_name ?? String(p.property_id),
        })),
      )
    },
    [draft.company_id],
  )

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: sync draft to incoming filters when the drawer opens
    if (isOpen) setDraft(filters)
  }, [isOpen, filters])

  useEffect(() => {
    if (!isAuthenticated || !isOpen) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: data-load-on-open populates the company dropdown (setState is async, after await)
    void loadCompanies('')
  }, [isAuthenticated, isOpen, loadCompanies])

  useEffect(() => {
    if (!isAuthenticated || !isOpen) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: data-load-on-open populates the property dropdown (setState is async, after await)
    void loadProperties('')
  }, [isAuthenticated, isOpen, loadProperties])

  const companyOptions = lockedCompanyId
    ? [
        {
          value: lockedCompanyId,
          label: user?.current_role?.company_name || lockedCompanyId,
        },
      ]
    : companies

  const propertyOptions = lockedPropertyId
    ? [
        {
          value: lockedPropertyId,
          label: user?.current_role?.property_name || lockedPropertyId,
        },
      ]
    : properties

  const patch = (partial: Partial<MrgReportFilterState>) => {
    setDraft((prev) => ({ ...prev, ...partial }))
  }

  return (
    <ListFilterDrawer
      isOpen={isOpen}
      onClose={onClose}
      onReset={onReset}
      onDone={() => {
        onApply(draft)
        onClose()
      }}
      testIdPrefix="mrg-report"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <label className={LABEL_CLASS}>Company</label>
          <SearchableSelect
            options={companyOptions}
            value={lockedCompanyId || draft.company_id || ''}
            onChange={(val) => {
              const company_id = Array.isArray(val) ? (val[0] ?? '') : val
              const company_name =
                companyOptions.find((o) => o.value === company_id)?.label ?? ''
              patch({
                company_id,
                company_name,
                property_id: '',
                property_name: '',
              })
            }}
            onSearch={lockedCompanyId ? undefined : loadCompanies}
            placeholder="All Company"
            disabled={Boolean(lockedCompanyId)}
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Property</label>
          <SearchableSelect
            options={propertyOptions}
            value={lockedPropertyId || draft.property_id || ''}
            onChange={(val) => {
              const property_id = Array.isArray(val) ? (val[0] ?? '') : val
              const property_name =
                propertyOptions.find((o) => o.value === property_id)?.label ??
                ''
              patch({ property_id, property_name })
            }}
            onSearch={lockedPropertyId ? undefined : loadProperties}
            placeholder="All Properties"
            disabled={Boolean(lockedPropertyId)}
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Revenue - Lease Start Dates</label>
          <ReportDateRangePicker
            startDate={draft.fromDt}
            endDate={draft.toDt}
            onChange={(start, end) =>
              patch({
                fromDt: start ? formatDateForAPI(start) : '',
                toDt: end ? formatDateForAPI(end) : '',
              })
            }
            customPresets={contractPresets}
            dropdownPanelClassName="[&>div:first-child>div]:!grid-cols-3 [&>div:first-child_button]:!min-w-0 [&>div:first-child_button]:!whitespace-normal [&>div:first-child_button]:leading-snug"
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Reimbursements - Paid Date</label>
          <ReportDateRangePicker
            startDate={draft.claimfromDt}
            endDate={draft.claimtoDt}
            onChange={(start, end) =>
              patch({
                claimfromDt: start ? formatDateForAPI(start) : '',
                claimtoDt: end ? formatDateForAPI(end) : '',
              })
            }
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Rental status</label>
          <SearchableSelect
            options={RENTAL_STATUS_OPTIONS}
            value={draft.rentalStatus}
            onChange={(val) =>
              patch({ rentalStatus: Array.isArray(val) ? val : [] })
            }
            placeholder="All"
            multiple
          />
        </div>
      </div>
    </ListFilterDrawer>
  )
}

export type { MrgReportFilterState } from '../types/filters'

